/*#
# hair-shading

Hair coloured the way a colourist describes it: a BASE colour and a
HIGHLIGHT colour. A material plugin tints the hair texture with the base,
leans its brighter strands toward the highlight (so a dark base with caramel
highlights reads as highlighted hair, not a flat tint), and colours the
strand sheen with the highlight too.

`HAIR_COLOURS` are presets in pairs, natural and dyed: each base with the
highlight it usually comes with, which a picker can then move anywhere.

`hairShading(material, { desaturate })` attaches it; `desaturate` reads a
texture that is already coloured (MakeHuman's hair and beards) as grey first,
so the same colours apply to every library's hair.
*/
/*{ "parent": "human-hair", "order": 10 }*/

import * as BABYLON from '@babylonjs/core'
import { registerMaterialPlugin } from './material-plugins.js'
import { parseColor } from './color.js'
import { HAIR_COLOURS } from './character-settings.js'

export { HAIR_COLOURS } from './character-settings.js'

const toLin = (c: number) =>
  c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
const toLinear = (c: number) =>
  c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)

/** An sRGB colour (anything `parseColor` reads) as linear `Color3`. */
function linear(colour: string): BABYLON.Color3 {
  const c = parseColor(colour) ?? { r: 0.3, g: 0.2, b: 0.15, a: 1 }
  return new BABYLON.Color3(toLinear(c.r), toLinear(c.g), toLinear(c.b))
}

/*
The hair textures average about this much (linear), so the base colour is
divided by it to come out as itself.
*/
const TEXTURE_MEAN = 0.42

export class HairShadingPlugin extends BABYLON.MaterialPluginBase {
  highlight = new BABYLON.Color3(0.5, 0.35, 0.2)
  /** How far the bright strands lean to the highlight, 0 to 1. */
  strength = 0.25
  desaturate = false
  /**
   * The texture's own mean brightness (linear), measured once it loads: the
   * texture is divided by it so the base colour comes out as itself whatever
   * library drew the hair (MakeHuman's are far darker than Quaternius').
   */
  mean = TEXTURE_MEAN
  private _isEnabled = false

  constructor(material: BABYLON.Material) {
    super(material, 'HairShading', 230, { HAIRSHADE: false, HAIRGREY: false })
  }

  get isEnabled(): boolean {
    return this._isEnabled
  }

  set isEnabled(enabled: boolean) {
    if (this._isEnabled === enabled) return
    this._isEnabled = enabled
    this.markAllDefinesAsDirty()
    this._enable(enabled)
  }

  prepareDefines(defines: BABYLON.MaterialDefines): void {
    defines.HAIRSHADE = this._isEnabled
    defines.HAIRGREY = this._isEnabled && this.desaturate
  }

  getClassName(): string {
    return 'HairShadingPlugin'
  }

  getUniforms(): {
    ubo: { name: string; size: number; type: string }[]
    fragment: string
  } {
    return {
      ubo: [
        { name: 'hairHighlight', size: 4, type: 'vec4' },
        { name: 'hairMean', size: 1, type: 'float' },
      ],
      fragment: `#ifdef HAIRSHADE
        uniform vec4 hairHighlight;
        uniform float hairMean;
      #endif`,
    }
  }

  bindForSubMesh(uniformBuffer: BABYLON.UniformBuffer): void {
    if (!this._isEnabled) return
    const h = this.highlight
    uniformBuffer.updateFloat4('hairHighlight', h.r, h.g, h.b, this.strength)
    uniformBuffer.updateFloat('hairMean', Math.max(0.03, this.mean))
  }

  getCustomCode(shaderType: string): { [pointName: string]: string } | null {
    if (shaderType !== 'fragment') return null
    return {
      CUSTOM_FRAGMENT_UPDATE_ALBEDO: `#if defined(HAIRSHADE) && defined(ALBEDO)
      {
        vec3 hT = albedoTexture.rgb;
        #ifdef GAMMAALBEDO
          hT = toLinearSpace(hT);
        #endif
        // The texture's brightness about its own mean: 1 is an average strand.
        float hL = dot(hT, vec3(0.2126, 0.7152, 0.0722)) / hairMean;
        // The texture read as grey, so the base colour is the colour (its own
        // colour, for libraries whose hair is drawn coloured, is dropped).
        surfaceAlbedo = vAlbedoColor.rgb * vec3(hL);
        // The brighter strands lean to the highlight, at their own brightness.
        float hK = smoothstep(0.9, 1.8, hL) * hairHighlight.a;
        surfaceAlbedo = mix(surfaceAlbedo, hairHighlight.rgb * hL, hK);
      }
      #endif`,
    }
  }
}

registerMaterialPlugin(
  'HairShadingPlugin',
  (material) => new HairShadingPlugin(material)
)

export interface HairShading {
  plugin: HairShadingPlugin
  /** Recolour: a base and a highlight, sRGB (anything `parseColor` reads). */
  setColours(base: string, highlight: string): void
}

/**
 * Colour a hair material by a base and a highlight. `desaturate` for a
 * texture that is already coloured.
 */
export function hairShading(
  material: BABYLON.PBRMaterial,
  options: { desaturate?: boolean; base?: string; highlight?: string } = {}
): HairShading {
  const plugin = material.pluginManager?.getPlugin(
    'HairShading'
  ) as HairShadingPlugin | null
  if (plugin == null) throw new Error('hair-shading: plugin not registered')
  plugin.desaturate = options.desaturate ?? false
  plugin.isEnabled = true
  const tex = material.albedoTexture
  if (tex) {
    const measure = () => {
      void tex.readPixels()?.then((data) => {
        const b = data as Uint8Array
        // Only where there is hair: the card's clear parts are not strands.
        let sum = 0
        let n = 0
        for (let i = 0; i < b.length; i += 4 * 29) {
          if (b[i + 3] < 128) continue
          sum +=
            0.2126 * toLin(b[i] / 255) +
            0.7152 * toLin(b[i + 1] / 255) +
            0.0722 * toLin(b[i + 2] / 255)
          n++
        }
        if (n > 0) plugin.mean = sum / n
      })
    }
    if (tex.isReady()) measure()
    else (tex as BABYLON.Texture).onLoadObservable?.addOnce(measure)
  }
  const setColours = (base: string, highlight: string) => {
    material.albedoColor = linear(base)
    plugin.highlight = linear(highlight)
    // The strand sheen takes the highlight.
    if (material.sheen.isEnabled)
      material.sheen.color = plugin.highlight.clone()
  }
  setColours(
    options.base ?? HAIR_COLOURS.brown.base,
    options.highlight ?? HAIR_COLOURS.brown.highlight
  )
  return { plugin, setColours }
}
